import type { AriaAttributes, KeyboardEvent, MouseEvent, ReactNode } from 'react';
import React, { Children, forwardRef, useEffect, useRef, useState } from 'react';

import { getChildrenOfType } from '../../../../utils/getChildrenOfType';
import PremiumLabel from '../../../labels/PremiumLabel/PremiumLabel';
import Tooltip from '../../../Tooltip/Tooltip';
import { LeftComponent } from './components/LeftComponent/LeftComponent';
import { RightComponent } from './components/RightComponent/RightComponent';
import {
  Container,
  LeftComponentContainer,
  NameContainer,
  PremiumLabelContainer,
  RightComponentContainer,
  RightContainer,
  StyledBasicParagraph,
  StyledBasicSubtitle,
  Wrapper,
} from './Item.styled';

export interface ItemProps extends Pick<AriaAttributes, 'aria-controls'> {
  children?: ReactNode | ReactNode[];
  /**
   * Property used for testing purposes
   */
  dataCy?: string;
  /**
   * Hides the indicator that it is active
   */
  hideIndicator?: boolean;
  /**
   * Hide the tooltip
   */
  hideTooltip?: boolean;
  /**
   * Indicates if the item is active
   */
  isActive?: boolean;
  /**
   * Indicates whether options should always be kept visible
   */
  keepRightComponentsVisible?: boolean;
  /**
   * Function that is executed when the banner is closed
   */
  onClick?: (e?: MouseEvent<HTMLDivElement>) => void;
  /**
   * Text to display on the Item
   */
  text: string;
}

export const Item = Object.assign(
  forwardRef<HTMLDivElement, ItemProps>(
    (
      {
        'aria-controls': ariaControls,
        children,
        dataCy,
        hideIndicator = false,
        hideTooltip = false,
        isActive = false,
        keepRightComponentsVisible = false,
        onClick,
        text = '',
      },
      ref,
    ) => {
      const [hasTooltip, setHasTooltip] = useState(false);
      const [shouldKeepHoverStyle, setShouldKeepHoverStyle] = useState(false);
      const tooltipRef = useRef(null);
      const textRef = useRef<HTMLParagraphElement>(null);
      const NameParagraph = isActive ? StyledBasicSubtitle : StyledBasicParagraph;
      const originalTooltipRef = tooltipRef.current;
      const arrayOfChildren = Children.toArray(children);

      useEffect(() => {
        if (textRef.current) {
          setHasTooltip(textRef.current.scrollWidth > textRef.current.clientWidth);
        }
      }, [text]);

      const handleTransitionEnd = () => {
        if (
          textRef.current &&
          textRef.current.scrollWidth > textRef.current.clientWidth &&
          !hasTooltip
        ) {
          setHasTooltip(true);
          // when Element rerendering wrapped by the tooltip, it should keep the hover state style
          setShouldKeepHoverStyle(true);
        }
      };

      const handleMouseLeave = () => {
        setShouldKeepHoverStyle(false);
      };

      const handleKeyDown = (e: KeyboardEvent<HTMLDivElement>) => {
        if (['Enter', ' '].includes(e.key)) {
          e.preventDefault();
          if (onClick) {
            onClick();
          }
        }
      };

      const handleClick = onClick;

      const leftComponentChild =
        children && getChildrenOfType(arrayOfChildren, [LeftComponent])[0];
      const premiumLabelChild =
        children && getChildrenOfType(arrayOfChildren, [PremiumLabel])[0];
      const rightComponentsChildren =
        children && getChildrenOfType(arrayOfChildren, [RightComponent]);

      const Element = (
        <Container
          $isClickable={!!onClick}
          $isActive={isActive && !hideIndicator}
          $shouldKeepHoverStyle={shouldKeepHoverStyle}
          aria-controls={ariaControls}
          aria-selected={isActive}
          data-cy={dataCy}
          onClick={handleClick}
          onKeyDown={handleKeyDown}
          onMouseLeave={handleMouseLeave}
          onTransitionEnd={handleTransitionEnd}
          ref={ref}
          role="tab"
          tabIndex={isActive ? 0 : -1}
        >
          {!!leftComponentChild && (
            <LeftComponentContainer>{leftComponentChild}</LeftComponentContainer>
          )}
          <NameContainer ref={originalTooltipRef}>
            <NameParagraph ref={textRef}>{text}</NameParagraph>
          </NameContainer>
          {!!premiumLabelChild && (
            <PremiumLabelContainer>{premiumLabelChild}</PremiumLabelContainer>
          )}
          {!!rightComponentsChildren && (
            <RightContainer
              $keepRightComponentsVisible={
                keepRightComponentsVisible || shouldKeepHoverStyle
              }
            >
              {rightComponentsChildren.map((rightComponent, i) => (
                // eslint-disable-next-line react/no-array-index-key
                <RightComponentContainer key={i}>
                  {rightComponent}
                </RightComponentContainer>
              ))}
            </RightContainer>
          )}
        </Container>
      );

      return hideTooltip || !hasTooltip ? (
        Element
      ) : (
        <Wrapper>
          <Tooltip
            placement={Tooltip.Position.RIGHT}
            text={text}
            tooltipTriggerReference={originalTooltipRef}
          >
            {Element}
          </Tooltip>
        </Wrapper>
      );
    },
  ),
  { LeftComponent, PremiumLabel, RightComponent },
);
